import { searchClient } from "@algolia/client-search";

export function useAlgolia<T>(options: AlgoliaOptions) {
  
  // Initialize options with defaults
  options = {
    index: undefined,
    initialSearch: {},
    useRouter: false,
    cacheSearch: false,
    universalSearch: false,
    ...options
  }
  const config = useRuntimeConfig()
  const ui = useUiStore()

  const algoliaRequester = {
    async send(payload: {
      url: string
      method: string
      headers?: Record<string, string>
      data?: unknown
    }) {
      const response = await fetch(payload.url, {
        method: payload.method,
        headers: payload.headers,
        body: payload.data as BodyInit | null | undefined,
        credentials: 'include'
      })

      return {
        content: await response.text(),
        status: response.status,
        isTimedOut: false
      }
    },
  }

  const protocol = config.public.apiUrl.startsWith('http://') ? 'http' : 'https'
  const algoliaApiUrl = config.public.apiUrl.replace(/^https?:\/\//, '').replace(/\/$/, '') + '/algolia'
  const algoliaClient = searchClient(
    '__app_id',
    '__api_key',
    {
      requester: algoliaRequester,
      hosts: [
        {
          url: algoliaApiUrl,
          accept: 'readWrite',
          protocol,
        }  
      ]
    }
  )

  const indexName = ref(options.index ? `${options.index}_${config.public.algoliaIndexEnvironment}` : '') as Ref<string>
  const initialSearch = ref() as Ref<AlgoliaSearch>
  const search = ref({}) as Ref<AlgoliaSearch>
  const results = ref({ hits: [] }) as Ref<any>
  const facets = reactive({}) as Record<string, AlgoliaFacet>

  const updatedSearch = computed(() => {
    // Check if the current search differs from the initial search
    return !isEqual(initialSearch.value, search.value)
  }) as Ref<boolean>

  // Initialize if index is given
  if (!options.universalSearch && options.index) {
    init(options.initialSearch)
  }

  async function init(search?: AlgoliaSearch) {
    // Clone search if given, because of reactivity issues
    const searchCopy = useCloneDeep(search) || {} as AlgoliaSearch
    // Use search parameters from UI store if given
    if (options.cacheSearch) {
      search = ui.getAlgoliaSearch(indexName.value, searchCopy)
    } else {
      search = searchCopy
    }
    // Are we connected to the router?
    if (options.useRouter) {
      const route = useRoute()
      // Read in any query parameters
      if (!isEmpty(route.query)) {
        search = {
          ...search,
          ..._fromQueryString(route.query)
        } as AlgoliaSearch
      }
    }
    // Execute search if given
    if (search) {
      await searchIndex(search)
    }
  }

  // Execute an index search
  function searchIndex(newSearch: AlgoliaSearch | undefined = undefined) {
    // Store search terms locally
    if (newSearch) { search.value = newSearch }
    // Store search terms in UI store
    if (options.cacheSearch) { ui.setAlgoliaSearch(indexName.value, search.value) }
    // Execute search
    return _updateSearch()
  }

  // Refine the current search with additional parameters
  function refineIndex(refinement: AlgoliaSearch) {
    // Process filters
    if (refinement.filters) {
      refinement.filters = {
        ...search.value.filters,
        ...refinement.filters
      }
    }
    // Add refinement to search
    search.value = {
      ...search.value,
      ...refinement
    }
    // Reset pagination if it's not a page change
    if (refinement.page === undefined || refinement.page === null) { _resetPagination() }
    // Clear out empty queries to properly match initial search
    if (refinement.query === '') { delete search.value.query }
    // Store search terms in UI store
    if (options.cacheSearch) { ui.setAlgoliaSearch(indexName.value, search.value) }
    // Execute search
    return _updateSearch().then(() => _updateFacets())
  }

  // Execute a universal search
  function universalSearch(query: string) {
    if (query.length < 2) return Promise.resolve([])

    // Initialize query items
    const algoliaIndices = {} as Record<string, any>
    algoliaIndices[`CompanyPrivate_${config.public.algoliaIndexEnvironment}`] = {
      name: 'Startups',
      restrictSearchableAttributes: ['name', 'fa_code'],
      attributesToRetrieve: ['name', 'slug', 'logo_src'],
      serializer: (hit: any) => ({
        name: hit.name,
        image: hit.logo_src,
        type: 'Startup',
        route: { name: 'company-view', params: { slug: hit.slug }}
      })
    }
    algoliaIndices[`Contact_${config.public.algoliaIndexEnvironment}`] = {
      name: 'People',
      restrictSearchableAttributes: ['name'],
      attributesToRetrieve: ['name', 'slug', 'contact_type', 'company_roles', 'program_roles', 'profile_image.small.url'],
      serializer: (hit: any) => {
        // Figure out a useful subhead
        let type = useCapitalize(hit.contact_type) as string
        if (type == 'Founder') {
          // Add companie to founders
          const company = hit.company_roles.map((role: any) => role.company.name).join(', ')
          type = `Founder / ${company}`
        } else if (type == 'Staff') {
          // Add programs to staff
          const program = hit.program_roles.map((role: any) => role.program.name).join(', ')
          type = `Staff / ${program}`
        }
        return {
          name: hit.name,
          image: hit.profile_image.small.url,
          type,
          route: { name: 'contact-view', params: { slug: hit.slug }}
        }
      }
    }    
    // Build algolia query
    const algoliaQueries = []
    for (const indexName in algoliaIndices) {
      const index = algoliaIndices[indexName]
      algoliaQueries.push({
        indexName,
        query,
        params: {
          hitsPerPage: 5,
          restrictSearchableAttributes: index.restrictSearchableAttributes,
          attributesToRetrieve: index.attributesToRetrieve
        }
      })
    }
    // Execute query and process results
    const algoliaResults: QuickSearchItem[] = []
    return algoliaClient.search(algoliaQueries).then((algoliaHits: any) => {
      algoliaHits.results.map((result: any) => {
        // Get index information
        const index = algoliaIndices[result.index]
        // Iterate through index hits
        result.hits.map((hit: any) => {
          // Call serializer on hit and add to results
          algoliaResults.push(index.serializer(hit))
        })
      })
      return algoliaResults || []
    })
  }

  // Clear the current search query
  function clearQuery() {
    search.value.query = ''
    return _updateSearch().then(() => _updateFacets())
  }

  // Add or remove a facet from the search
  function toggleFacet(facetName: string, facetValue: string) {
    // Add facet to search
    if (!search.value.filters) {
      search.value.filters = {}
    }
    if (!search.value.filters[facetName]) {
      search.value.filters[facetName] = []
    }
    // Toggle facet value
    if (search.value.filters[facetName].includes(facetValue)) {
      // Remove from list
      search.value.filters[facetName] = search.value.filters[facetName].filter((v) => v !== facetValue)
      // Remove empty facets
      if (search.value.filters[facetName].length === 0) {
        delete search.value.filters[facetName]
      }
    } else {
      // Add to list
      search.value.filters[facetName].push(facetValue)
    }
    // Reset pagination
    _resetPagination()
    // Execute search
    return _updateSearch().then(() => _updateFacets())
  }

  // Clear all selected facets
  function clearFacets() {
    search.value = options.initialSearch as AlgoliaSearch
    return _updateSearch().then(() => _updateFacets())
  }

  // Get facet values for a given facet(s)
  function getFacetValues(requestedFacets: string | string[], options = { label: '', substitutions: {}, sorting: [], isHierarchical: false }) {
    // Don't accept options for multiple facets
    if (Array.isArray(requestedFacets)) {
      options = {
        label: '',
        substitutions: {},
        sorting: [],
        isHierarchical: false
      }
    // Normalize single facet into array
    } else {
      requestedFacets = [requestedFacets]
    }
    // Process all requested facets
    return Promise.all(requestedFacets.map((facetName) => {
      // Initialize facet and set isRefined status
      _initFacet(facetName, options)
      // Execute search
      return _getFacets(facets[facetName]).then((result) => {
        if (!result) return
        if (!result.facets) return

        // Process returned facets
        if (facets[facetName].isHierarchical) {
          _parseHierarchicalFacets(facetName, result.facets)
        } else {
          _parseFacets(facetName, result.facets)
        }
      })
    }))
  }

  // Return the named facet
  function facet(facetName: string) {
    return facets[facetName] || { values: [], isRefined: false }
  }

  // Return the label for the given facet
  function getLabelForFacet(facetName: string) {
    const facet = facets[facetName]
    return facet ? facet.label : ''
  }

  // Return the label for the given facet value
  function getLabelForFacetValue(facetName: string, facetValue: string) {
    const facet = facets[facetName]
    if (!facet) return ''
    const value = facet.values.find((v) => v.value === facetValue)
    return value ? value.label : ''
  }

  // Return the value for the given facet label
  function getValueForFacetLabel(facetName: string, facetLabel: string) {
    const facet = facets[facetName]
    if (!facet) return ''
    const value = facet.values.find((v) => v.label === facetLabel)
    return value ? value.value : ''
  }

  // Getters

  function hits() {
    return results.value?.hits || []
  }

  // Extract pagination from results
  function pagination() {
    if (!results.value) return { page: 0, nbPages: 0, hitsPerPage: 0, nbHits: 0 }
    return {
      page: results.value.page || 0,
      nbPages: results.value.nbPages || 0,
      hitsPerPage: results.value.hitsPerPage || 0,
      nbHits: results.value.nbHits || 0
    }
  }

  // Internal

  // Call Algolia to get search results
  function _updateSearch() {
    // Execute search
    return algoliaClient.searchSingleIndex({
      indexName: indexName.value,
      searchParams: _algoliaSearch()
    }).then((result) => {
      // Assign results
      results.value = result
      // Execute routing callback
      if (options.useRouter) _updateRoute()
    })
  }

  // Call Algolia to get facets
  function _updateFacets() {
    const regularFacets = Object.keys(facets).filter((f) => !facets[f].isHierarchical)
    const hierarchicalFacets = Object.keys(facets).filter((f) => facets[f].isHierarchical)
    // Get facet values
    return getFacetValues(regularFacets).then(() => {
      // Get hierarchical facet values
      hierarchicalFacets.map((facet) => {
        getFacetValues(facet)
      })
    })
  }

  // Reset pagination to first page
  function _resetPagination() {
    // Reset pagination to first page
    if (search.value.page) { search.value.page = 0 }
  }

  // Convert internal search model to Algolia search model
  function _algoliaSearch() {
    // Normalize search terms
    return {
      ...search.value,
      // Convert filters to Algolia format
      filters: _convertFilters(search.value.filters),
      // Default page to 0 (first page)
      page: search.value.page || 0,
      // Default hitsPerPage to 10
      hitsPerPage: search.value.hitsPerPage || 20
    }
  }

  // Return a query string that can be reassembled into a search
  function _toQueryString() {
    let queryTerms = []
    // Add search terms
    if (search.value.query) {
      queryTerms.push(`q=${search.value.query}`)
    }
    // Add filters
    if (search.value.filters && !isEmpty(search.value.filters)) {
      for (const [facet, values] of Object.entries(search.value.filters)) {
        queryTerms.push(`${facet}=${values.join(',')}`)
      }
    }
    // Add page
    if (search.value.page) {
      queryTerms.push(`page=${search.value.page + 1}`)
    }
    return queryTerms.join('&')
  }

  // Parse the current search from a route's query object
  function _fromQueryString(query: any) {
    let querySearch = {} as AlgoliaSearch
    // Extract query
    if (query.q) {
      querySearch.query = query.q
      delete query.q
    }
    // Extract page
    if (query.page) {
      querySearch.page = parseInt(query.page) - 1
      delete query.page
    }
    // Anything left over is filters
    if (!isEmpty(query)) {
      Object.keys(query).map((facet) => {
        querySearch.filters = querySearch.filters || {}
        querySearch.filters[facet] = query[facet].split(',')
      })
    }
    return querySearch as AlgoliaSearch
  }

  function _updateRoute() {
    const route = useRoute()
    useRouter().replace(`${route.path}?${_toQueryString()}`)
  }

  // Convert internal filters to Algolia format
  function _convertFilters(filters: Record<string, string[]> | undefined): string {
    if (!filters) return ''
    // Convert filters to Algolia format
    let algoliaFilters = Object.keys(filters).map((facet) => {
      return filters[facet].map((value) => `${facet}:"${value}"`).join(' OR ')
    }).join(') AND (')
    // Wrap in parentheses
    return algoliaFilters ? `(${algoliaFilters})` : ''
  }

  // Call Algolia to get facet values
  function _getFacets(facet: AlgoliaFacet) {
    const facetName = facet.name
    // Get top two levels if hierarchical
    const facets = facet.isHierarchical ? [`${facetName}.lvl0`, `${facetName}.lvl1`] : [facetName]
    // Remove self from search
    const updatedFilters = useClone(search.value.filters)
    if (updatedFilters) {
      delete updatedFilters[facetName]
    }
    return algoliaClient.searchSingleIndex({
      indexName: indexName.value,
      searchParams: {
        ..._algoliaSearch(),
        hitsPerPage: 0,
        filters: _convertFilters(updatedFilters),
        facets,
        sortFacetValuesBy: 'alpha'
      }
    })
  }

  // Initialize a facet
  function _initFacet(facetName: string, options: AlgoliaFacetOptions) {
    // Initialize facet in local store if necessary
    if (!facets[facetName]) {
      facets[facetName] = {
        name: facetName,
        values: [] as AlgoliaFacetValue[],
        isRefined: false,
        isHierarchical: options.isHierarchical || false,
        label: options.label,
        substitutions: options.substitutions || {},
        sorting: options.sorting || {}
      }
    }
    // Is this facet refined?
    let isRefined = false
    if (facets[facetName].isHierarchical) {
      isRefined = search.value.filters ?
        (Object.keys(search.value.filters || {})).includes(`${facetName}.lvl0`) :
        false
    } else {
      isRefined = search.value.filters ?
        (Object.keys(search.value.filters || {})).includes(facetName) :
        false
    }
      // Update refined status
    facets[facetName].isRefined = isRefined
  }

  // Take a list of facets from Algolia and update the local store
  function _parseFacets(facetName: string, algoliaFacets: any) {
    const facet = facets[facetName]
    // Clear existing values
    facet.values = []
    // Process each value in the facet
    for (const [facetValue, facetCount] of Object.entries(algoliaFacets[facetName] || {})) {
      facet.values.push({
        label: facet.substitutions[facetValue] || useStartCase(facetValue),
        value: facetValue,
        count: facetCount as number || 0,
        isRefined: facet.isRefined && search.value.filters ?
          (search.value.filters[facetName] || []).includes(facetValue) :
          false
      })
    }
    // Sort
    if (isEmpty(facet.sorting)) {
      // Default sorting is count descending, then value ascending (alphabetical)
      // Note: this is done by running the sorts in reverse order
      facets[facetName].values = useSortBy(facets[facetName].values, 'value')
      facets[facetName].values = useSortBy(facets[facetName].values, (v) => v.count * -1)
    } else {
      // Sort according to the value's position in sorting array
      // Note: values not in sorting array will be sorted to the top
      facet.values = useSortBy(facet.values, (v) => facet.sorting.indexOf(v.value))
    }
    // Sort by refined
    facets[facetName].values = useSortBy(facets[facetName].values, (v) => v.isRefined ? 0 : 1)      
  }

  // Take a list of hierarchical facets from Algolia and update the local store
  function _parseHierarchicalFacets(facetName: string, algoliaFacets: any) {
    const facet = facets[facetName]
    const topLevel = algoliaFacets[`${facetName}.lvl0`] || {}
    const subLevel = algoliaFacets[`${facetName}.lvl1`] || {}
    // Clear existing values
    facet.values = []
    // Process each value in the top level
    for (const [facetValue, facetCount] of Object.entries(topLevel)) {
      // Check if this facet is refined
      let isRefined = false
      if (facet.isRefined && search.value.filters && search.value.filters[`${facetName}.lvl0`]) {
        isRefined = search.value.filters ?
          search.value.filters[`${facetName}.lvl0`].includes(facetValue) :
          false
      }
      // Add top-level facet values
      facet.values.push({
        label: facet.substitutions[facetValue] || useStartCase(facetValue),
        value: facetValue,
        count: facetCount as number || 0,
        children: [],
        isRefined
      })
    }
    // Sort by name
    facet.values = useSortBy(facet.values, 'value')
    // Process each value in the sub level
    for (const [compoundValue, facetCount] of Object.entries(subLevel)) {
      const [firstValue, secondValue] = compoundValue.split(' > ')
      // Check if this facet is refined
      let isRefined = false
      if (facet.isRefined && search.value.filters && search.value.filters[`${facetName}.lvl1`]) {
        isRefined = search.value.filters ?
          search.value.filters[`${facetName}.lvl1`].includes(compoundValue) :
          false
      }
      // Add sub-level facet values
      const parent = facet.values.find((v) => v.value === firstValue)
      parent && parent.children && parent.children.push({
        label: facet.substitutions[secondValue] || useStartCase(secondValue),
        value: secondValue,
        count: facetCount as number || 0,
        isRefined
      })
    }
  }

  return {
    search,
    updatedSearch,
    hits,
    pagination,
    searchIndex,
    refineIndex,
    universalSearch,
    clearQuery,
    toggleFacet,
    clearFacets,
    facet,
    getFacetValues,
    getLabelForFacet,
    getLabelForFacetValue,
    getValueForFacetLabel,
  }
}
